import React, {useEffect, useRef, useState} from 'react';

type Props = {
  length?: number;
  disabled?: boolean;
  onComplete: (code: string) => void;
};

// 認証コードを 1 桁ずつの欄で受け取る。dentalrevolution の OtpInput を Tailwind で移した（#159）。
// 入力で次の欄へ進み、空の欄の Backspace で前の欄へ戻る。貼り付けはまとめて入れ、全桁そろったら onComplete を呼ぶ。
// コードの自動入力（autocomplete="one-time-code"）は 1 つの欄に全桁をまとめて入れるので、maxLength で切らずに後ろの欄へ振り分ける。
// 入れ直させるときは親が key を変えて作り直す。
export const OtpInput = ({length = 6, disabled = false, onComplete}: Props) => {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const update = (next: string[], focusIndex: number) => {
    setDigits(next);
    inputRefs.current[Math.min(focusIndex, length - 1)]?.focus();
    if (next.every((d) => d !== '')) onComplete(next.join(''));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const current = digits[index];
    let value = e.target.value.replace(/\D/g, '');
    // 入っている桁に打ち足したときは、元の桁を除いて新しく入った分だけを使う（打ち直しで置き換える）
    if (current && value.length > 1) {
      if (value.startsWith(current)) value = value.slice(1);
      else if (value.endsWith(current)) value = value.slice(0, -1);
    }
    if (!value) return;

    const entered = value.slice(0, length - index);
    const next = [...digits];
    entered.split('').forEach((digit, i) => {
      next[index + i] = digit;
    });
    update(next, index + entered.length);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key !== 'Backspace') return;

    e.preventDefault();
    const next = [...digits];
    if (next[index]) {
      next[index] = '';
      setDigits(next);
    } else if (index > 0) {
      next[index - 1] = '';
      setDigits(next);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text/plain')
      .replace(/\D/g, '')
      .slice(0, length);
    if (!pasted) return;

    const next = Array(length)
      .fill('')
      .map((_, i) => pasted[i] ?? '');
    update(next, pasted.length);
  };

  return (
    <div className="flex justify-center gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(input) => {
            inputRefs.current[index] = input;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`認証コード ${index + 1}桁目`}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className="size-12 rounded border border-neutral-300 bg-white text-center text-2xl font-medium text-neutral-700 focus:border-primary focus:outline-none disabled:opacity-50"
        />
      ))}
    </div>
  );
};
